import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button } from "gold-ui-kit";
import AppShell from "../components/classic/AppShell";
import { useAuth } from "../context/AuthContext";
import { isPageDisabled } from "../data/disabledPages";
import {
  Users,
  CalendarCheck,
  FileText,
  ShoppingBag,
  Calculator,
  Receipt,
  ArrowRight,
  Lock,
} from "lucide-react";

const MODULE_GROUPS = [
  {
    title: "Personel",
    modules: [
      {
        icon: Users,
        title: "Personel Listem",
        description: "Çalışan personelleri görüntüle, yeni personel ekle, düzenle veya sistemden çıkar.",
        path: "/personel",
        tone: "bg-blue-50 text-blue-600 ring-blue-100",
        badge: "İnsan Kaynakları",
        testId: "dashboard-personel-btn",
      },
      {
        icon: CalendarCheck,
        title: "Personel Devam Listesi",
        description: "QR, kart ve manuel devam kayıtlarını izle, yeni manuel giriş veya çıkış kaydı aç.",
        path: "/devam",
        tone: "bg-emerald-50 text-emerald-600 ring-emerald-100",
        badge: "Yoklama & Mesai",
        testId: "dashboard-devam-btn",
      },
      {
        icon: FileText,
        title: "Personel İstifa Formu",
        description: "Ayrılan personeller için resmi çıkış ve gerekçeli A4 istifa formunu kaydet.",
        path: "/istifa",
        tone: "bg-rose-50 text-rose-600 ring-rose-100",
        badge: "Resmi Evrak",
        testId: "dashboard-istifa-btn",
      },
    ],
  },
  {
    title: "Operasyon",
    modules: [
      {
        icon: ShoppingBag,
        title: "Manav Listesi Girişi",
        description: "Manav reyonu için günlük ürün sipariş ve miktar listesini doldur ve yazdır.",
        path: "/manav",
        tone: "bg-amber-50 text-amber-600 ring-amber-100",
        badge: "Sipariş & Reyon",
        testId: "dashboard-manav-btn",
      },
      {
        icon: Calculator,
        title: "Veri Girişi",
        description: "Avans, Mesai ve Kesinti gün bilgilerini Excel benzeri pratik tabloyla gir.",
        path: "/veri-girisi",
        tone: "bg-indigo-50 text-indigo-600 ring-indigo-100",
        badge: "Finans & Muhasebe",
        testId: "dashboard-verigirisi-btn",
      },
      {
        icon: Receipt,
        title: "Z-Raporu İşlemleri",
        description: "Günlük kasa Z-Raporu girişi, banka POS, gider/borç ve kasa mutabakatı.",
        path: "/zrapor",
        tone: "bg-purple-50 text-purple-600 ring-purple-100",
        badge: "Kasa & Kapanış",
        testId: "dashboard-zrapor-btn",
      },
    ],
  },
];

const ModuleCard = ({ icon: Icon, title, description, tone, badge, testId, disabled, onOpen }) => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onOpen}
    onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onOpen();
      }
    }}
    title={disabled ? "Bu modül şu anda pasif" : undefined}
    data-testid={testId}
    className={`group relative flex flex-col justify-between rounded-2xl border bg-white p-5 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
      disabled
        ? "border-dashed border-slate-200 cursor-not-allowed"
        : "border-slate-200/80 shadow-sm hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-500/5 cursor-pointer"
    }`}
  >
    <div>
      <div className="mb-4 flex items-start justify-between gap-3">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 ${
            disabled ? "bg-slate-50 text-slate-300 ring-slate-100" : tone
          }`}
        >
          <Icon size={21} />
        </span>
        {disabled ? (
          <Badge size="sm" radius="full" variant="outline" color="default">
            <span className="flex items-center gap-1">
              <Lock size={11} /> Pasif
            </span>
          </Badge>
        ) : (
          <Badge size="sm" radius="full" variant="soft" color="default">
            {badge}
          </Badge>
        )}
      </div>
      <h3 className={`mb-1 text-[15px] font-bold tracking-tight ${disabled ? "text-slate-400" : "text-slate-900"}`}>
        {title}
      </h3>
      <p className={`text-xs leading-relaxed ${disabled ? "text-slate-400" : "text-slate-500"}`}>{description}</p>
    </div>

    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-3 text-xs font-semibold">
      {disabled ? (
        <span className="text-slate-400">Şu anda kullanıma kapalı</span>
      ) : (
        <>
          <span className="text-slate-700 group-hover:text-amber-700">Modülü Aç</span>
          <ArrowRight size={16} className="text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-amber-600" />
        </>
      )}
    </div>
  </div>
);

const WelcomeDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const name = user?.fullName || user?.username || "Kullanıcı";

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";
  const todayDateStr = new Date().toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const allModules = MODULE_GROUPS.flatMap((g) => g.modules);
  const activeCount = allModules.filter((m) => !isPageDisabled(m.path)).length;

  return (
    <AppShell pageId="dashboard">
      <div className="flex w-full flex-col gap-8 p-3 sm:p-6">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 px-6 py-7 text-white sm:px-8 sm:py-9">
          <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-amber-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-amber-300/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="mb-2 text-xs font-semibold text-amber-300">{todayDateStr}</div>
              <h2 className="text-2xl font-black tracking-tight sm:text-3xl">
                {greeting}, {name}
              </h2>
              <p className="mt-1.5 max-w-xl text-sm text-slate-300">
                Şube işlemlerinizi yönetmek için aşağıdaki modüllerden birini seçin.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button color="warning" radius="lg" onClick={() => navigate("/devam")}>
                <span className="flex items-center gap-2">
                  <CalendarCheck size={16} /> Devam Takibi
                </span>
              </Button>
              <Button variant="outline" color="warning" radius="lg" onClick={() => navigate("/personel")}>
                <span className="flex items-center gap-2">
                  <Users size={16} /> Personel
                </span>
              </Button>
            </div>
          </div>

          <div className="relative mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] text-slate-400">Aktif Modül</div>
              <div className="text-xl font-bold">
                {activeCount}
                <span className="text-sm font-medium text-slate-500"> / {allModules.length}</span>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] text-slate-400">Oturum</div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> Çevrimiçi
              </div>
            </div>
            <div className="col-span-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 sm:col-span-1">
              <div className="text-[11px] text-slate-400">Yardım</div>
              <div className="text-sm font-semibold">Üst bardaki Yardım butonu</div>
            </div>
          </div>
        </section>

        {MODULE_GROUPS.map((group) => (
          <section key={group.title} className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-slate-500">{group.title}</h3>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {group.modules.map((m) => (
                <ModuleCard key={m.path} {...m} disabled={isPageDisabled(m.path)} onOpen={() => navigate(m.path)} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </AppShell>
  );
};

export default WelcomeDashboard;
